import { api } from "../utils/api";
import { refreshNow } from "./poller";
import { ChartData, ChartParams, ChartSince, ChatMessage, OrderInfo, Widget } from "./types";

export function errorText(e: any): string {
  // FastAPI's validation errors (422) carry a list of objects as detail
  const detail = e?.response?.data?.detail;
  if (detail != null) return typeof detail === "string" ? detail : JSON.stringify(detail);
  return e?.message ?? String(e);
}

// every action refreshes the polled data right away instead of waiting for the next tick
async function action<T>(request: Promise<{ data: T }>): Promise<T> {
  try {
    return (await request).data;
  } finally {
    refreshNow();
  }
}

export const placeLimit = (conId: number, size: number, price: number) =>
  action(api.post<OrderInfo>("/ibkr/limit-order", { conId, size, price }));

// step: price move per step (at least one tick), stepDuration: seconds between steps
export const placeAutoLadder = (conId: number, size: number, step: number, stepDuration: number) =>
  action(api.post<OrderInfo>("/ibkr/auto-ladder", { conId, size, step, step_duration: stepDuration }));

export const modifyOrder = (orderId: number, change: { price?: number; size?: number }) =>
  action(api.post<OrderInfo>(`/ibkr/orders/${orderId}/modify`, change));

export const cancelOrder = (orderId: number) => action(api.post<OrderInfo>(`/ibkr/orders/${orderId}/cancel`));

export const cancelAllOrders = () => action(api.post<OrderInfo[]>("/ibkr/cancel-orders"));

// a chart window loads all bars once, then polls the bars since its last one
// (the backend keeps the chart subscribed while it's polled)
export const chartData = async (params: ChartParams) => (await api.post<ChartData>("/ibkr/chart", params)).data;

export const CHART_POLL_TIMEOUT = 4000;
export const chartSince = async (params: ChartParams, since: number | null, underSince: number | null) =>
  (await api.post<ChartSince>("/ibkr/chart/since", { ...params, since, underSince }, { timeout: CHART_POLL_TIMEOUT })).data;

// the chat as the user sees it, sent along with every message: the backend
// looks up each window's live state from its params (ibkr_agent.py _describe_screen)
const screenOf = (messages: ChatMessage[]) =>
  messages.map(({ role, text, ts, local, source, widgets }) => ({
    role, text, ts, local, source, widgets: widgets.map(({ type, params }) => ({ type, params })),
  }));

export async function agentChat(sessionId: string, message: string, messages: ChatMessage[]) {
  // source: "auto" = command shortcut answered straight from IBKR, "llm" = the agent
  const res = await api.post<{ text: string; widgets: Omit<Widget, "id">[]; source: "auto" | "llm" }>(
    "/ibkr/agent/chat", { sessionId, message, screen: screenOf(messages) });
  return res.data;
}
